import React, { useState, useEffect, createContext, useContext } from 'react';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// All available themes
export const THEMES = {
  // Default (no special theme)
  default: {
    id: 'default',
    name: 'Default',
    icon: '🎨',
    description: 'Standard appearance',
    category: 'regular',
    colors: null,
    effects: null
  },

  // ============ HOLIDAY THEMES ============

  christmas: {
    id: 'christmas',
    name: 'Christmas',
    icon: '🎄',
    description: 'Festive holiday decorations',
    category: 'holiday',
    autoEnable: { month: 11, startDay: 1, endDay: 25 }, // Dec 1-25
    colors: {
      primary: '#c41e3a',
      secondary: '#1a472a',
      accent: '#ffd700',
      background: 'linear-gradient(180deg, #1a472a 0%, #2d5a3f 50%, #1a472a 100%)',
      text: '#ffffff'
    },
    effects: ['snow', 'lights']
  },

  newYear: {
    id: 'newYear',
    name: 'New Year',
    icon: '🎆',
    description: 'Celebrate the new year!',
    category: 'holiday',
    autoEnable: [
      { month: 11, startDay: 26, endDay: 31 }, // Dec 26-31
      { month: 0, startDay: 1, endDay: 5 }     // Jan 1-5
    ],
    colors: {
      primary: '#ffd700',
      secondary: '#1a1a2e',
      accent: '#c0c0c0',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      text: '#ffffff'
    },
    effects: ['confetti', 'sparkle']
  },

  valentines: {
    id: 'valentines',
    name: "Valentine's Day",
    icon: '💕',
    description: 'Love is in the air',
    category: 'holiday',
    autoEnable: { month: 1, startDay: 10, endDay: 14 }, // Feb 10-14
    colors: {
      primary: '#e91e63',
      secondary: '#880e4f',
      accent: '#ff80ab',
      background: 'linear-gradient(180deg, #880e4f 0%, #c2185b 50%, #880e4f 100%)',
      text: '#ffffff'
    },
    effects: ['hearts']
  },

  stPatricks: {
    id: 'stPatricks',
    name: "St. Patrick's Day",
    icon: '☘️',
    description: 'Feeling lucky!',
    category: 'holiday',
    autoEnable: { month: 2, startDay: 14, endDay: 17 }, // Mar 14-17
    colors: {
      primary: '#2e7d32',
      secondary: '#1b5e20',
      accent: '#ffd700',
      background: 'linear-gradient(180deg, #1b5e20 0%, #388e3c 50%, #1b5e20 100%)',
      text: '#ffffff'
    },
    effects: ['shamrocks', 'rainbow']
  },

  easter: {
    id: 'easter',
    name: 'Easter',
    icon: '🐰',
    description: 'Spring celebration',
    category: 'holiday',
    autoEnable: { month: 3, startDay: 1, endDay: 21 }, // Approx Easter window Apr 1-21
    colors: {
      primary: '#ab47bc',
      secondary: '#7b1fa2',
      accent: '#81d4fa',
      background: 'linear-gradient(180deg, #e1bee7 0%, #ce93d8 50%, #e1bee7 100%)',
      text: '#4a148c'
    },
    effects: ['eggs', 'bunny']
  },

  independenceDay: {
    id: 'independenceDay',
    name: 'Independence Day',
    icon: '🎇',
    description: 'Celebrate America!',
    category: 'holiday',
    autoEnable: { month: 6, startDay: 1, endDay: 4 }, // July 1-4
    colors: {
      primary: '#b71c1c',
      secondary: '#0d47a1',
      accent: '#ffffff',
      background: 'linear-gradient(180deg, #b71c1c 0%, #1565c0 50%, #b71c1c 100%)',
      text: '#ffffff'
    },
    effects: ['fireworks', 'stars']
  },

  halloween: {
    id: 'halloween',
    name: 'Halloween',
    icon: '🎃',
    description: 'Spooky season!',
    category: 'holiday',
    autoEnable: { month: 9, startDay: 15, endDay: 31 }, // Oct 15-31
    colors: {
      primary: '#ff6f00',
      secondary: '#1a1a1a',
      accent: '#7b1fa2',
      background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
      text: '#ff6f00'
    },
    effects: ['bats', 'pumpkins', 'ghosts']
  },

  thanksgiving: {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    icon: '🦃',
    description: 'Grateful season',
    category: 'holiday',
    autoEnable: { month: 10, startDay: 15, endDay: 28 }, // Nov 15-28
    colors: {
      primary: '#e65100',
      secondary: '#bf360c',
      accent: '#ffc107',
      background: 'linear-gradient(180deg, #5d4037 0%, #795548 50%, #5d4037 100%)',
      text: '#ffffff'
    },
    effects: ['leaves']
  },

  // ============ NEON/SURVEYING THEMES ============

  neonLime: {
    id: 'neonLime',
    name: 'Neon Lime',
    icon: '💚',
    description: 'Bright lime green neon',
    category: 'neon',
    colors: {
      primary: '#00ff00',
      secondary: '#0a0a0a',
      accent: '#7fff00',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      text: '#00ff00',
      glow: '0 0 10px #00ff00, 0 0 20px #00ff00, 0 0 30px #00ff00'
    },
    effects: ['glow']
  },

  electricBlue: {
    id: 'electricBlue',
    name: 'Electric Blue',
    icon: '💙',
    description: 'Electric blue neon',
    category: 'neon',
    colors: {
      primary: '#00ffff',
      secondary: '#0a0a0a',
      accent: '#00bfff',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #0a1929 50%, #0a0a0a 100%)',
      text: '#00ffff',
      glow: '0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff'
    },
    effects: ['glow']
  },

  hotPink: {
    id: 'hotPink',
    name: 'Hot Pink',
    icon: '💗',
    description: 'Hot pink neon',
    category: 'neon',
    colors: {
      primary: '#ff1493',
      secondary: '#0a0a0a',
      accent: '#ff69b4',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0a14 50%, #0a0a0a 100%)',
      text: '#ff1493',
      glow: '0 0 10px #ff1493, 0 0 20px #ff1493, 0 0 30px #ff1493'
    },
    effects: ['glow']
  },

  sunsetOrange: {
    id: 'sunsetOrange',
    name: 'Sunset Orange',
    icon: '🧡',
    description: 'Vibrant orange neon',
    category: 'neon',
    colors: {
      primary: '#ff6600',
      secondary: '#0a0a0a',
      accent: '#ff9900',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0f0a 50%, #0a0a0a 100%)',
      text: '#ff6600',
      glow: '0 0 10px #ff6600, 0 0 20px #ff6600, 0 0 30px #ff6600'
    },
    effects: ['glow']
  },

  radioactive: {
    id: 'radioactive',
    name: 'Radioactive',
    icon: '☢️',
    description: 'Toxic green glow',
    category: 'neon',
    colors: {
      primary: '#adff2f',
      secondary: '#0a0a0a',
      accent: '#ccff00',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #0f1a0a 50%, #0a0a0a 100%)',
      text: '#adff2f',
      glow: '0 0 10px #adff2f, 0 0 20px #adff2f, 0 0 30px #adff2f'
    },
    effects: ['glow', 'pulse']
  },

  surveyorYellow: {
    id: 'surveyorYellow',
    name: 'Surveyor Yellow',
    icon: '🦺',
    description: 'Classic safety vest yellow',
    category: 'neon',
    colors: {
      primary: '#ffff00',
      secondary: '#1a1a1a',
      accent: '#ffd700',
      background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d1a 50%, #1a1a1a 100%)',
      text: '#ffff00',
      glow: '0 0 10px #ffff00, 0 0 20px #ffff00, 0 0 30px #ffff00'
    },
    effects: ['glow']
  },

  purpleHaze: {
    id: 'purpleHaze',
    name: 'Purple Haze',
    icon: '💜',
    description: 'Deep purple neon',
    category: 'neon',
    colors: {
      primary: '#9400d3',
      secondary: '#0a0a0a',
      accent: '#ba55d3',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #14091a 50%, #0a0a0a 100%)',
      text: '#9400d3',
      glow: '0 0 10px #9400d3, 0 0 20px #9400d3, 0 0 30px #9400d3'
    },
    effects: ['glow']
  },

  fireEngine: {
    id: 'fireEngine',
    name: 'Fire Engine Red',
    icon: '❤️',
    description: 'Bold red neon',
    category: 'neon',
    colors: {
      primary: '#ff0000',
      secondary: '#0a0a0a',
      accent: '#ff4444',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0a0a 50%, #0a0a0a 100%)',
      text: '#ff0000',
      glow: '0 0 10px #ff0000, 0 0 20px #ff0000, 0 0 30px #ff0000'
    },
    effects: ['glow']
  }
};

// Check if a date falls within a theme's auto-enable period
const isInAutoEnablePeriod = (theme) => {
  if (!theme.autoEnable) return false;

  const now = new Date();
  const month = now.getMonth();
  const day = now.getDate();

  const checkPeriod = (period) => {
    return month === period.month && day >= period.startDay && day <= period.endDay;
  };

  if (Array.isArray(theme.autoEnable)) {
    return theme.autoEnable.some(checkPeriod);
  }

  return checkPeriod(theme.autoEnable);
};

// Get the auto-enabled theme for current date
export const getAutoEnabledTheme = () => {
  for (const [key, theme] of Object.entries(THEMES)) {
    if (isInAutoEnablePeriod(theme)) {
      return key;
    }
  }
  return null;
};

// ============ VISUAL EFFECTS COMPONENTS ============

// Snowflake for Christmas
const Snowflake = ({ style }) => (
  <div style={{
    position: 'fixed',
    color: 'white',
    fontSize: style.fontSize || '20px',
    textShadow: '0 0 5px rgba(255,255,255,0.8)',
    pointerEvents: 'none',
    zIndex: 9999,
    animation: `snowfall ${style.duration}s linear infinite`,
    left: style.left,
    top: '-20px',
    animationDelay: style.delay,
    opacity: style.opacity
  }}>
    ❄
  </div>
);

// Snow effect
const SnowEffect = () => {
  const snowflakes = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 5 + Math.random() * 10,
    delay: `${Math.random() * 5}s`,
    fontSize: `${10 + Math.random() * 20}px`,
    opacity: 0.3 + Math.random() * 0.7
  }));

  return (
    <>
      {snowflakes.map(flake => (
        <Snowflake key={flake.id} style={flake} />
      ))}
    </>
  );
};

// Christmas Lights
const ChristmasLights = () => {
  const colors = ['#ff0000', '#00ff00', '#ffff00', '#ff00ff', '#00ffff', '#ff6600'];
  const lights = Array.from({ length: 30 }, (_, i) => ({
    color: colors[i % colors.length],
    delay: i * 0.2
  }));

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '8px',
      display: 'flex',
      justifyContent: 'space-around',
      zIndex: 9998,
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)',
      paddingTop: '2px'
    }}>
      {lights.map((light, i) => (
        <div
          key={i}
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: light.color,
            boxShadow: `0 0 10px ${light.color}, 0 0 20px ${light.color}`,
            animation: `twinkle 1s ease-in-out infinite`,
            animationDelay: `${light.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Heart effect for Valentine's
const HeartsEffect = () => {
  const hearts = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 6 + Math.random() * 8,
    delay: `${Math.random() * 5}s`,
    fontSize: `${15 + Math.random() * 25}px`,
    opacity: 0.4 + Math.random() * 0.6
  }));

  return (
    <>
      {hearts.map(heart => (
        <div key={heart.id} style={{
          position: 'fixed',
          color: '#ff69b4',
          fontSize: heart.fontSize,
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `floatUp ${heart.duration}s linear infinite`,
          left: heart.left,
          bottom: '-30px',
          animationDelay: heart.delay,
          opacity: heart.opacity
        }}>
          {Math.random() > 0.5 ? '❤️' : '💕'}
        </div>
      ))}
    </>
  );
};

// Shamrock effect for St. Patrick's
const ShamrocksEffect = () => {
  const shamrocks = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 7 + Math.random() * 8,
    delay: `${Math.random() * 5}s`,
    fontSize: `${18 + Math.random() * 20}px`,
    opacity: 0.5 + Math.random() * 0.5
  }));

  return (
    <>
      {shamrocks.map(shamrock => (
        <div key={shamrock.id} style={{
          position: 'fixed',
          color: '#2e7d32',
          fontSize: shamrock.fontSize,
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `floatDown ${shamrock.duration}s linear infinite`,
          left: shamrock.left,
          top: '-30px',
          animationDelay: shamrock.delay,
          opacity: shamrock.opacity
        }}>
          ☘️
        </div>
      ))}
    </>
  );
};

// Rainbow for St. Patrick's
const RainbowEffect = () => (
  <div style={{
    position: 'fixed',
    top: '10%',
    right: '-10%',
    width: '400px',
    height: '200px',
    borderRadius: '200px 200px 0 0',
    background: 'linear-gradient(180deg, #ff0000 0%, #ff7f00 15%, #ffff00 30%, #00ff00 45%, #0000ff 60%, #4b0082 75%, #9400d3 90%)',
    opacity: 0.3,
    pointerEvents: 'none',
    zIndex: 9997
  }} />
);

// Easter eggs
const EasterEggsEffect = () => {
  const colors = ['#ff9999', '#99ff99', '#9999ff', '#ffff99', '#ff99ff', '#99ffff'];
  const eggs = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 8 + Math.random() * 6,
    delay: `${Math.random() * 5}s`,
    color: colors[i % colors.length],
    fontSize: `${20 + Math.random() * 15}px`
  }));

  return (
    <>
      {eggs.map(egg => (
        <div key={egg.id} style={{
          position: 'fixed',
          fontSize: egg.fontSize,
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `floatDown ${egg.duration}s linear infinite`,
          left: egg.left,
          top: '-40px',
          animationDelay: egg.delay,
          opacity: 0.7
        }}>
          🥚
        </div>
      ))}
    </>
  );
};

// Fireworks for July 4th and New Year
const FireworksEffect = () => {
  const [fireworks, setFireworks] = useState([]);

  useEffect(() => {
    const createFirework = () => {
      const id = Date.now();
      const newFirework = {
        id,
        left: `${10 + Math.random() * 80}%`,
        top: `${10 + Math.random() * 40}%`,
        color: ['#ff0000', '#ffffff', '#0000ff', '#ffd700', '#ff69b4'][Math.floor(Math.random() * 5)]
      };
      setFireworks(prev => [...prev, newFirework]);
      setTimeout(() => {
        setFireworks(prev => prev.filter(f => f.id !== id));
      }, 1500);
    };

    const interval = setInterval(createFirework, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {fireworks.map(fw => (
        <div key={fw.id} style={{
          position: 'fixed',
          left: fw.left,
          top: fw.top,
          fontSize: '40px',
          pointerEvents: 'none',
          zIndex: 9999,
          animation: 'explode 1.5s ease-out forwards'
        }}>
          {['✨', '🎆', '🎇'][Math.floor(Math.random() * 3)]}
        </div>
      ))}
    </>
  );
};

// Confetti for New Year
const ConfettiEffect = () => {
  const confetti = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 4 + Math.random() * 6,
    delay: `${Math.random() * 3}s`,
    color: ['#ffd700', '#c0c0c0', '#ff69b4', '#00ff00', '#00ffff'][i % 5],
    size: 8 + Math.random() * 10
  }));

  return (
    <>
      {confetti.map(c => (
        <div key={c.id} style={{
          position: 'fixed',
          width: `${c.size}px`,
          height: `${c.size}px`,
          backgroundColor: c.color,
          borderRadius: Math.random() > 0.5 ? '50%' : '0',
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `confettiFall ${c.duration}s linear infinite`,
          left: c.left,
          top: '-20px',
          animationDelay: c.delay,
          opacity: 0.8
        }} />
      ))}
    </>
  );
};

// Sparkle effect
const SparkleEffect = () => {
  const sparkles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: Math.random() * 2
  }));

  return (
    <>
      {sparkles.map(s => (
        <div key={s.id} style={{
          position: 'fixed',
          left: s.left,
          top: s.top,
          color: '#ffd700',
          fontSize: '20px',
          pointerEvents: 'none',
          zIndex: 9998,
          animation: `sparkle 2s ease-in-out infinite`,
          animationDelay: `${s.delay}s`
        }}>
          ✨
        </div>
      ))}
    </>
  );
};

// Bats for Halloween
const BatsEffect = () => {
  const bats = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    startLeft: `${-10 + Math.random() * 20}%`,
    top: `${5 + Math.random() * 30}%`,
    duration: 8 + Math.random() * 6,
    delay: `${Math.random() * 8}s`,
    size: 25 + Math.random() * 20
  }));

  return (
    <>
      {bats.map(bat => (
        <div key={bat.id} style={{
          position: 'fixed',
          left: bat.startLeft,
          top: bat.top,
          fontSize: `${bat.size}px`,
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `flyAcross ${bat.duration}s linear infinite`,
          animationDelay: bat.delay
        }}>
          🦇
        </div>
      ))}
    </>
  );
};

// Pumpkins for Halloween
const PumpkinsEffect = () => (
  <div style={{
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: '60px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
    zIndex: 9997,
    pointerEvents: 'none'
  }}>
    {Array.from({ length: 8 }, (_, i) => (
      <span key={i} style={{
        fontSize: `${30 + Math.random() * 20}px`,
        animation: 'pumpkinGlow 2s ease-in-out infinite',
        animationDelay: `${i * 0.3}s`
      }}>
        🎃
      </span>
    ))}
  </div>
);

// Ghost effect for Halloween
const GhostsEffect = () => {
  const ghosts = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 10 + Math.random() * 8,
    delay: `${Math.random() * 6}s`
  }));

  return (
    <>
      {ghosts.map(ghost => (
        <div key={ghost.id} style={{
          position: 'fixed',
          left: ghost.left,
          bottom: '100px',
          fontSize: '35px',
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `ghostFloat ${ghost.duration}s ease-in-out infinite`,
          animationDelay: ghost.delay,
          opacity: 0.7
        }}>
          👻
        </div>
      ))}
    </>
  );
};

// Falling leaves for Thanksgiving
const LeavesEffect = () => {
  const leaves = ['🍂', '🍁', '🍃'];
  const items = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: 8 + Math.random() * 8,
    delay: `${Math.random() * 5}s`,
    leaf: leaves[i % leaves.length],
    fontSize: `${18 + Math.random() * 18}px`
  }));

  return (
    <>
      {items.map(item => (
        <div key={item.id} style={{
          position: 'fixed',
          left: item.left,
          top: '-30px',
          fontSize: item.fontSize,
          pointerEvents: 'none',
          zIndex: 9999,
          animation: `leafFall ${item.duration}s linear infinite`,
          animationDelay: item.delay,
          opacity: 0.8
        }}>
          {item.leaf}
        </div>
      ))}
    </>
  );
};

// Stars effect for July 4th
const StarsEffect = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '10px',
    display: 'flex',
    justifyContent: 'space-around',
    zIndex: 9998,
    background: 'linear-gradient(to bottom, rgba(0,0,50,0.5), transparent)',
    paddingTop: '3px'
  }}>
    {Array.from({ length: 25 }, (_, i) => (
      <span key={i} style={{
        color: i % 3 === 0 ? '#ff0000' : i % 3 === 1 ? '#ffffff' : '#0000ff',
        fontSize: '14px',
        animation: 'starTwinkle 1s ease-in-out infinite',
        animationDelay: `${i * 0.1}s`
      }}>
        ⭐
      </span>
    ))}
  </div>
);

// Render effects based on theme
const ThemeEffects = ({ effects }) => {
  if (!effects || effects.length === 0) return null;

  return (
    <>
      {effects.includes('snow') && <SnowEffect />}
      {effects.includes('lights') && <ChristmasLights />}
      {effects.includes('hearts') && <HeartsEffect />}
      {effects.includes('shamrocks') && <ShamrocksEffect />}
      {effects.includes('rainbow') && <RainbowEffect />}
      {effects.includes('eggs') && <EasterEggsEffect />}
      {effects.includes('fireworks') && <FireworksEffect />}
      {effects.includes('confetti') && <ConfettiEffect />}
      {effects.includes('sparkle') && <SparkleEffect />}
      {effects.includes('bats') && <BatsEffect />}
      {effects.includes('pumpkins') && <PumpkinsEffect />}
      {effects.includes('ghosts') && <GhostsEffect />}
      {effects.includes('leaves') && <LeavesEffect />}
      {effects.includes('stars') && <StarsEffect />}
    </>
  );
};

// Generate CSS for a theme
const generateThemeCSS = (theme) => {
  if (!theme.colors) return '';

  const { primary, secondary, accent, background, text, glow } = theme.colors;
  const isNeon = theme.category === 'neon';

  return `
    .theme-${theme.id} .sidebar {
      background: ${background} !important;
    }

    .theme-${theme.id} .sidebar-header h1 {
      color: ${accent || primary} !important;
      ${glow ? `text-shadow: ${glow};` : ''}
    }

    .theme-${theme.id} .nav-item {
      color: ${text} !important;
    }

    .theme-${theme.id} .nav-item.active {
      background-color: ${primary} !important;
      border-left-color: ${accent} !important;
      ${isNeon ? 'color: #000 !important;' : ''}
      ${glow ? `box-shadow: ${glow};` : ''}
    }

    .theme-${theme.id} .nav-item.active svg {
      ${isNeon ? 'color: #000 !important;' : ''}
    }

    .theme-${theme.id} .nav-item:hover {
      background-color: ${primary}88 !important;
      ${isNeon ? 'color: #000 !important;' : ''}
    }

    .theme-${theme.id} .nav-item:hover svg {
      ${isNeon ? 'color: #000 !important;' : ''}
    }

    .theme-${theme.id} .btn-primary {
      background-color: ${primary} !important;
      border-color: ${primary} !important;
      color: ${isNeon ? '#000' : '#fff'} !important;
      ${glow ? `box-shadow: ${glow};` : ''}
    }

    .theme-${theme.id} .btn-primary:hover {
      background-color: ${accent} !important;
      border-color: ${accent} !important;
    }

    ${isNeon ? `
    .theme-${theme.id} .sidebar .nav-section-title {
      color: ${primary} !important;
      ${glow ? `text-shadow: ${glow};` : ''}
    }

    .theme-${theme.id} .sidebar .nav-item svg {
      color: ${primary} !important;
    }

    .theme-${theme.id} .main-content {
      background: #0f0f0f !important;
    }

    .theme-${theme.id} .page-header h1 {
      color: ${primary} !important;
      ${glow ? `text-shadow: ${glow};` : ''}
    }

    .theme-${theme.id} .card {
      background: #1a1a1a !important;
      border: 1px solid ${primary}44 !important;
    }

    .theme-${theme.id} .table th {
      color: ${primary} !important;
      background: #1a1a1a !important;
    }

    .theme-${theme.id} .table td {
      color: #ddd !important;
      border-color: ${primary}33 !important;
    }
    ` : ''}
  `;
};

// Check if a saved theme should be auto-reset (holiday theme out of season)
const shouldResetTheme = (savedTheme) => {
  if (!savedTheme || !THEMES[savedTheme]) return true;

  const theme = THEMES[savedTheme];

  // If it's not a holiday theme, keep it
  if (theme.category !== 'holiday') return false;

  // If it's a holiday theme, check if it's still in season
  return !isInAutoEnablePeriod(theme);
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('appTheme');

    // If there's a saved theme
    if (saved && THEMES[saved]) {
      // Check if it's a holiday theme that's no longer in season
      if (shouldResetTheme(saved)) {
        // Holiday is over - check if there's a new holiday in season
        const autoTheme = getAutoEnabledTheme();
        if (autoTheme) {
          localStorage.setItem('appTheme', autoTheme);
          return autoTheme;
        }
        // No holiday in season, reset to default
        localStorage.setItem('appTheme', 'default');
        return 'default';
      }
      // Theme is still valid (either not a holiday, or holiday still in season)
      return saved;
    }

    // No saved theme - check for auto-enabled holiday theme
    const autoTheme = getAutoEnabledTheme();
    return autoTheme || 'default';
  });

  const [effectsEnabled, setEffectsEnabled] = useState(() => {
    const saved = localStorage.getItem('themeEffects');
    return saved !== 'false'; // Default to true
  });

  const theme = THEMES[currentTheme] || THEMES.default;

  useEffect(() => {
    localStorage.setItem('appTheme', currentTheme);

    // Remove all theme classes
    Object.keys(THEMES).forEach(key => {
      document.body.classList.remove(`theme-${key}`);
    });

    // Add current theme class
    if (currentTheme !== 'default') {
      document.body.classList.add(`theme-${currentTheme}`);
    }
  }, [currentTheme]);

  useEffect(() => {
    localStorage.setItem('themeEffects', effectsEnabled.toString());
  }, [effectsEnabled]);

  const setTheme = (themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  const toggleEffects = () => {
    setEffectsEnabled(!effectsEnabled);
  };

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      theme,
      setTheme,
      effectsEnabled,
      toggleEffects,
      themes: THEMES,
      autoEnabledTheme: getAutoEnabledTheme()
    }}>
      {currentTheme !== 'default' && (
        <>
          {effectsEnabled && theme.effects && <ThemeEffects effects={theme.effects} />}
          <style>{`
            @keyframes snowfall {
              0% { transform: translateY(-20px) rotate(0deg); }
              100% { transform: translateY(100vh) rotate(360deg); }
            }

            @keyframes twinkle {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.4; transform: scale(0.8); }
            }

            @keyframes floatUp {
              0% { transform: translateY(0) rotate(0deg); }
              100% { transform: translateY(-100vh) rotate(360deg); }
            }

            @keyframes floatDown {
              0% { transform: translateY(0) rotate(0deg); }
              100% { transform: translateY(100vh) rotate(360deg); }
            }

            @keyframes explode {
              0% { transform: scale(0); opacity: 1; }
              50% { transform: scale(1.5); opacity: 1; }
              100% { transform: scale(2); opacity: 0; }
            }

            @keyframes confettiFall {
              0% { transform: translateY(0) rotate(0deg); }
              100% { transform: translateY(100vh) rotate(720deg); }
            }

            @keyframes sparkle {
              0%, 100% { opacity: 0; transform: scale(0.5); }
              50% { opacity: 1; transform: scale(1.2); }
            }

            @keyframes flyAcross {
              0% { transform: translateX(0); }
              100% { transform: translateX(120vw); }
            }

            @keyframes pumpkinGlow {
              0%, 100% { filter: brightness(1); }
              50% { filter: brightness(1.5) drop-shadow(0 0 10px #ff6600); }
            }

            @keyframes ghostFloat {
              0%, 100% { transform: translateY(0) translateX(0); }
              25% { transform: translateY(-30px) translateX(10px); }
              50% { transform: translateY(-50px) translateX(-10px); }
              75% { transform: translateY(-30px) translateX(10px); }
            }

            @keyframes leafFall {
              0% { transform: translateY(0) rotate(0deg) translateX(0); }
              25% { transform: translateY(25vh) rotate(90deg) translateX(20px); }
              50% { transform: translateY(50vh) rotate(180deg) translateX(-20px); }
              75% { transform: translateY(75vh) rotate(270deg) translateX(20px); }
              100% { transform: translateY(100vh) rotate(360deg) translateX(0); }
            }

            @keyframes starTwinkle {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.5; transform: scale(0.8); }
            }

            @keyframes neonPulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }

            ${generateThemeCSS(theme)}
          `}</style>
        </>
      )}
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
